/**
 * Shift Event Breakdown Report API.
 *
 * Same report Lambda and the same body shape as `fetchCaregiverReport` and
 * `fetchEventReport` — only `rep_type` differs. Deliberately parallel: all three
 * reports read the same event population through the same filters, so a
 * divergence in what gets sent would be a divergence in what the reports mean,
 * and this report's totals are supposed to tie out against Alert Details.
 *
 * Filter options and reason codes are NOT re-fetched here. `fetchFilterOptions`
 * from the alert-details module already returns everything the shared
 * ReportFilterDrawer needs, and a second copy would drift.
 */

import { postReport } from '../../../../services/api.js';
import { reportFilterFields, responseTimeField, timeWindowField } from '../../../../utils/reportRequestBody.js';

/**
 * Run the shift report.
 *
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate — 'YYYY-MM-DD' or 'YYYY-MM-DD HH:MM:SS'
 * @param {string} opts.endDate
 * @param {object} opts.filters   — the page's report-level filter selections
 * @returns {Promise<object>} { records, hours, boundaries, totals, summary, overlimit, url, ErrorCode }
 */
export async function fetchShiftReport({ campusId, username, startDate, endDate, filters = {} }) {
    const body = {
        rep_type: 'shiftReport',
        username: username || '',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        /* Empty on purpose. The event report sends "1,4,10,11,12,13,15"; a shift
           owns every event raised during it, maintenance included, and the
           backend reads '' as "no category filter". */
        event_category_filter: '',
        ...reportFilterFields(filters),
        resp_filter_items: responseTimeField(filters),
        /* Minutes from midnight, half-open. [0, 1440] is all day and the query
           treats it as a no-op.

           This page does not offer the time-of-day control (see the page's
           `showTimeWindow`), so this is always the all-day default from here.
           Sent anyway, because the field is part of the shared request shape and
           omitting it would make this the one report whose body differs. */
        time_window: timeWindowField(filters),
    };

    const res = await postReport(body, '[fetchShiftReport] request failed');
    if (res.ErrorCode) return { ErrorCode: res.ErrorCode };
    const data = res.data;

    return {
        records: Array.isArray(data?.records) ? data.records : [],
        hours: Array.isArray(data?.hours) ? data.hours : [],
        boundaries: Array.isArray(data?.boundaries) ? data.boundaries : [],
        totals: Array.isArray(data?.totals) ? data.totals : [],
        summary: data?.summary || {},
        overlimit: Boolean(data?.overlimit),
        url: data?.url || '',
    };
}
